<script lang="ts" setup>
/**
 * UI-kit component
 * Category: Images
 * Name: AppImage
 * Responsive image. With a Strapi `media` object it renders an <img> + webp
 * srcset built from the media's formats; with raw `src` + `srcset` it renders a
 * <picture> with an avif <source> (bundled backgrounds). `priority` marks an
 * above-the-fold/LCP image (eager + fetchpriority=high); otherwise lazy. Strips
 * Vite's dev-only `?t=` cache-buster to avoid SSR/client hydration drift.
 */
// types
import type { AppImageProps } from '../types/interfaces'
// hooks
import { useAppImage } from '../composables/use-app-image.hook'

defineOptions({
  name: 'AppImage',
})

const props = withDefaults(defineProps<AppImageProps>(), {
  media: undefined,
  src: '',
  srcset: '',
  sizes: undefined,
  alt: '',
  width: undefined,
  height: undefined,
  priority: false,
})

const {
  refUsePicture,
  refSrc,
  refImgSrcset,
  refAvifSrcset,
  refWidth,
  refHeight,
  refAlt,
  refLoading,
  refFetchpriority,
} = useAppImage({ props })
</script>

<template>
  <picture
    v-if="refUsePicture"
    class="ui-app-image"
  >
    <source
      :srcset="refAvifSrcset"
      type="image/avif"
    >
    <img
      :src="refSrc"
      :alt="refAlt"
      :width="refWidth"
      :height="refHeight"
      :loading="refLoading"
      :fetchpriority="refFetchpriority"
      decoding="async"
    >
  </picture>
  <img
    v-else
    :src="refSrc"
    :srcset="refImgSrcset || undefined"
    :sizes="refImgSrcset ? sizes : undefined"
    :alt="refAlt"
    :width="refWidth"
    :height="refHeight"
    :loading="refLoading"
    :fetchpriority="refFetchpriority"
    decoding="async"
  >
</template>

<style lang="scss" scoped src="../styles/_app-image.scss" />
